import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import type { BoardView, Workflow, WorkflowFolder, WorkflowPatch } from "../api/types";

const BASE = "/api/workflows";

/**
 * Data layer for the Workflows production board (the `Workflow` entities — one
 * level up from Projects). Returns the owner's workflows plus create / update /
 * delete actions. Edits are OPTIMISTIC — the flexible PATCH serves every inline
 * change (status drag, assign / NA, dates, links, runtime, rename, batches), so
 * the grid updates instantly and reconciles on settle.
 *
 * (Distinct from `lib/workflows.ts`, which is the QC-module filter registry now
 * surfaced under the "Modules" sidebar section.)
 */
export function useWorkflowBoard() {
  const qc = useQueryClient();
  const key = ["workflows"];

  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: () => api<{ workflows: Workflow[] }>(BASE).then((r) => r.workflows),
  });
  const list = data ?? [];

  const setCache = (fn: (cur: Workflow[]) => Workflow[]) =>
    qc.setQueryData<Workflow[]>(key, (cur) => fn(cur ?? []));
  const invalidate = () => qc.invalidateQueries({ queryKey: key });

  const createM = useMutation({
    mutationFn: (body: WorkflowPatch) =>
      api<Workflow>(BASE, { method: "POST", body: JSON.stringify(body) }),
    onSuccess: (wf) => setCache((cur) => [...cur, wf]),
    onSettled: invalidate,
  });

  const updateM = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: WorkflowPatch }) =>
      api<Workflow>(`${BASE}/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
    onMutate: ({ id, patch }) =>
      setCache((cur) => cur.map((w) => (w.id === id ? { ...w, ...patch } : w))),
    onSettled: invalidate,
  });

  const deleteM = useMutation({
    mutationFn: (id: string) => api(`${BASE}/${id}`, { method: "DELETE" }),
    onMutate: (id) => setCache((cur) => cur.filter((w) => w.id !== id)),
    onSettled: invalidate,
  });

  return {
    list,
    isLoading,
    createWorkflow: (body: WorkflowPatch) => createM.mutateAsync(body),
    updateWorkflow: (id: string, patch: WorkflowPatch) => updateM.mutate({ id, patch }),
    deleteWorkflow: (id: string) => deleteM.mutate(id),
  };
}

/**
 * The board's folders — a named grouping of rows. Separate from the workflow
 * list on purpose: a folder is not a job, so it must never be able to reach a
 * count, a filter or a Timeline lane by accident.
 *
 * Deleting a folder unfiles its rows server-side, so both caches are refreshed.
 */
export function useWorkflowFolders() {
  const qc = useQueryClient();
  const key = ["workflow-folders"];

  const { data, isSuccess } = useQuery({
    queryKey: key,
    queryFn: () => api<{ folders: WorkflowFolder[] }>(`${BASE}/folders`).then((r) => r.folders),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: key });
  const invalidateBoth = () => { invalidate(); qc.invalidateQueries({ queryKey: ["workflows"] }); };

  const createM = useMutation({
    mutationFn: (name: string) =>
      api<WorkflowFolder>(`${BASE}/folders`, { method: "POST", body: JSON.stringify({ name }) }),
    onSettled: invalidate,
  });
  const renameM = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api<WorkflowFolder>(`${BASE}/folders/${id}`, { method: "PATCH", body: JSON.stringify({ name }) }),
    onMutate: ({ id, name }) =>
      qc.setQueryData<WorkflowFolder[]>(key, (cur) =>
        (cur ?? []).map((f) => (f.id === id ? { ...f, name } : f))),
    onSettled: invalidate,
  });
  const deleteM = useMutation({
    mutationFn: (id: string) =>
      api<{ unfiled: number }>(`${BASE}/folders/${id}`, { method: "DELETE" }),
    onSettled: invalidateBoth,
  });

  return {
    folders: data ?? [],
    // "None yet" and "not loaded yet" are both an empty array, and anything
    // that prunes a folder selection has to tell them apart: pruning too early
    // clears a valid filter, pruning never leaves a dead one in place.
    foldersLoaded: isSuccess,
    createFolder: (name: string) => createM.mutateAsync(name),
    renameFolder: (id: string, name: string) => renameM.mutate({ id, name }),
    deleteFolder: (id: string) => deleteM.mutateAsync(id),
  };
}

/**
 * Saved views — named arrangements of the board, stored on the account so they
 * follow you to another machine (and can be shared with a teammate later).
 */
export function useBoardViews() {
  const qc = useQueryClient();
  const key = ["workflow-views"];

  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: () => api<{ views: BoardView[] }>(`${BASE}/views`).then((r) => r.views),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: key });

  const createM = useMutation({
    mutationFn: (body: { name: string; state: Record<string, unknown>; is_default?: boolean }) =>
      api<BoardView>(`${BASE}/views`, { method: "POST", body: JSON.stringify(body) }),
    onSettled: invalidate,
  });
  const updateM = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<Omit<BoardView, "id">> }) =>
      api<BoardView>(`${BASE}/views/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
    onMutate: ({ id, patch }) =>
      qc.setQueryData<BoardView[]>(key, (cur) => (cur ?? []).map((v) =>
        // Default is exclusive, so the optimistic update has to demote the
        // others too — otherwise two views wear the badge until the refetch.
        v.id === id ? { ...v, ...patch } : patch.is_default ? { ...v, is_default: false } : v)),
    onSettled: invalidate,
  });
  const deleteM = useMutation({
    mutationFn: (id: string) => api(`${BASE}/views/${id}`, { method: "DELETE" }),
    onMutate: (id) => qc.setQueryData<BoardView[]>(key, (cur) => (cur ?? []).filter((v) => v.id !== id)),
    onSettled: invalidate,
  });

  return {
    views: data ?? [],
    viewsLoading: isLoading,
    saveView: (name: string, state: Record<string, unknown>, is_default = false) =>
      createM.mutateAsync({ name, state, is_default }),
    updateView: (id: string, patch: Partial<Omit<BoardView, "id">>) => updateM.mutateAsync({ id, patch }),
    deleteView: (id: string) => deleteM.mutateAsync(id),
  };
}
